import { useCallback, useState } from "react";
import { runMigrationJob } from "../api/jobs";
import type {
  MigrationRequest,
  MigrationResult,
  Planner,
  ProgressEvent,
  Stage,
} from "../types";

/** Every stage the pipeline can run, in the order it runs them. */
const ALL_STAGES: { id: Stage; label: string }[] = [
  { id: "export", label: "Read Lex bot" },
  { id: "introspect", label: "Snapshot workspace" },
  { id: "plan", label: "Plan flows" },
  { id: "apply", label: "Create in CX Designer" },
  { id: "deploy", label: "Build & deploy" },
  { id: "contactFlow", label: "Contact flow" },
];

export type StageState = "pending" | "running" | "done" | "skipped" | "failed";

export interface StageProgress {
  state: StageState;
  message?: string;
  detail?: string;
}

/** One thing that happened, for the live log beside the stages. */
export interface Activity {
  id: number;
  stage: Stage;
  message: string;
  detail?: string;
  index?: number;
  total?: number;
  outcome?: ProgressEvent["outcome"];
}

export interface StageView {
  id: Stage;
  label: string;
  progress: StageProgress;
}

/**
 * The stages a given run will actually perform.
 *
 * Deploy and the contact flow only happen when asked for, and showing them
 * greyed out for a run that will never reach them is noise - worse, it makes
 * a finished migration look like it stopped short.
 */
function plannedStages(body: MigrationRequest): Stage[] {
  const ids: Stage[] = ["export", "introspect", "plan", "apply"];
  if (body.deploy) ids.push("deploy");
  if (body.contactFlow) ids.push("contactFlow");
  return ids;
}

export function useMigration() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<MigrationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [planned, setPlanned] = useState<Stage[]>([]);
  const [stages, setStages] = useState<Record<string, StageProgress>>({});
  const [activity, setActivity] = useState<Activity[]>([]);

  const apply = useCallback((event: ProgressEvent, order: Stage[]) => {
    /*
     * A step says what a stage is doing, not that it has finished.
     *
     * Handled before anything else because the stage update below treats
     * every status that is not "start" or "skip" as "done" - so a step
     * falling through would tick a stage that is still running.
     */
    if (event.status === "step") {
      setActivity((log) => [
        ...log,
        {
          id: log.length,
          stage: event.stage,
          message: event.message,
          detail: event.detail,
          index: event.index,
          total: event.total,
          outcome: event.outcome,
        },
      ]);
      return;
    }

    setStages((current) => {
      const next = { ...current };

      next[event.stage] = {
        state:
          event.status === "start"
            ? "running"
            : event.status === "skip"
              ? "skipped"
              : "done",
        message: event.message,
        detail: event.detail,
      };

      /*
       * Advance to the following stage straight away.
       *
       * The pipeline is strictly sequential and emits "X done" immediately
       * before "Y start", so by the time a poll tells us X finished, Y is
       * already running. Waiting for the next poll to say so leaves the whole
       * list idle for up to two seconds after every stage - the work carries
       * on but nothing on screen moves, which reads as a stall.
       *
       * Only stages this run will actually perform are advanced into, so a
       * migration that stops at "apply" never shows deploy spinning.
       */
      if (event.status === "done") {
        const position = order.indexOf(event.stage);
        const following = position >= 0 ? order[position + 1] : undefined;
        if (following && (next[following]?.state ?? "pending") === "pending") {
          next[following] = { state: "running" };
        }
      }

      return next;
    });
  }, []);

  const run = useCallback(
    async (planner: Planner, body: MigrationRequest) => {
      if (running) return;

      const order = plannedStages(body);
      setRunning(true);
      setResult(null);
      setError(null);
      setPlanned(order);
      setActivity([]);

      /*
       * The first stage starts spinning on the click, not on the first poll.
       *
       * The server has to accept the job and the client then waits for a poll
       * to come back before any real event exists, so the truthful-but-useless
       * version of this list sits completely still for several seconds after
       * the button is pressed - which reads as "nothing happened". The
       * migration genuinely is reading the Lex bot during that window; saying
       * so immediately is accurate, and the real event overwrites it with the
       * server's own message as soon as it lands.
       */
      setStages(
        Object.fromEntries(
          order.map((id, index) => [
            id,
            { state: (index === 0 ? "running" : "pending") as StageState },
          ]),
        ),
      );

      let failed = false;
      try {
        setResult(
          await runMigrationJob(planner, body, (event) => apply(event, order)),
        );
      } catch (e) {
        failed = true;
        setError((e as Error).message);
      } finally {
        setRunning(false);
        // Anything still marked running when the job ends never reported its
        // own completion; leaving it spinning would be a lie. So would calling
        // it done when the run is what stopped it - that stage is where the
        // migration got to, and the error belongs against it.
        const settled: StageState = failed ? "failed" : "done";
        setStages((current) =>
          Object.fromEntries(
            Object.entries(current).map(([id, s]) => [
              id,
              s.state === "running" ? { ...s, state: settled } : s,
            ]),
          ),
        );
      }
    },
    [apply, running],
  );

  /**
   * Forgets the last migration entirely.
   *
   * `run` clears this state too, but only once a new run starts - so between
   * choosing another bot and pressing Convert, the screen would still be
   * showing the previous run's finished stages and result. Picking a bot that
   * has not been converted yet and seeing every step already ticked is the
   * worst version of that. Ignored mid-run, where there is nothing safe to
   * forget.
   */
  const reset = useCallback(() => {
    if (running) return;
    setResult(null);
    setError(null);
    setPlanned([]);
    setStages({});
    setActivity([]);
  }, [running]);

  /** The stages to show, in order, with their state. */
  const view: StageView[] = planned.map((id) => ({
    id,
    label: ALL_STAGES.find((s) => s.id === id)!.label,
    progress: stages[id] ?? { state: "pending" },
  }));

  return { run, reset, running, result, error, stages: view, activity };
}
